import { CheckCircle, Error as ErrorIcon } from "@mui/icons-material"
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Sstv2ApiInfo,
  useLazyPostSstv2TokenTestQuery,
  useGetSstv2LockersQuery,
  useGetSstv2ReservationsQuery,
  useGetSstv2SqsConfigsQuery,
} from "src/features/partners/partnersApi"

type SaSstv2DiagnosticsProps = {
  apiInfo?: Sstv2ApiInfo
  isLoading: boolean
}

export const SaSstv2Diagnostics: FC<SaSstv2DiagnosticsProps> = ({
  apiInfo,
  isLoading,
}) => {
  const { t } = useTranslation()

  // Token test
  const [triggerTokenTest, tokenTestResult] = useLazyPostSstv2TokenTestQuery()
  const [tokenTestRan, setTokenTestRan] = useState(false)

  const handleTokenTest = useCallback(() => {
    setTokenTestRan(true)
    triggerTokenTest()
  }, [triggerTokenTest])

  // Endpoint health probes — use small page fetches to check reachability
  const [probeLockers, setProbeLockers] = useState(false)
  const [probeReservations, setProbeReservations] = useState(false)
  const [probeSqs, setProbeSqs] = useState(false)

  const qLockers = useGetSstv2LockersQuery(
    { page: 1, count: 1 },
    { skip: !probeLockers },
  )
  const qReservations = useGetSstv2ReservationsQuery(
    { page: 1, count: 1 },
    { skip: !probeReservations },
  )
  const qSqs = useGetSstv2SqsConfigsQuery(
    { page: 1, count: 1 },
    { skip: !probeSqs },
  )

  const handleProbeAll = useCallback(() => {
    setProbeLockers(true)
    setProbeReservations(true)
    setProbeSqs(true)
  }, [])

  if (isLoading) {
    return (
      <Stack direction="column" spacing={2} width="100%">
        <Skeleton variant="rectangular" width="100%" height={120} />
        <Skeleton variant="rectangular" width="100%" height={120} />
        <Skeleton variant="rectangular" width="100%" height={120} />
      </Stack>
    )
  }

  return (
    <Stack data-ui="UI-SL2W-233" direction="column" spacing={3} width="100%">
      {/* OAuth2 Token Section */}
      <Card variant="outlined">
        <CardHeader title="OAuth2 Token" />
        <CardContent>
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2">{t("status")}:</Typography>
              {apiInfo?.tokenError ? (
                <Chip
                  label={`Error: ${apiInfo.tokenError}`}
                  color="error"
                  size="small"
                  icon={<ErrorIcon />}
                />
              ) : (
                <Chip
                  label={apiInfo?.tokenStatus || "Unknown"}
                  color={apiInfo?.tokenStatus ? "success" : "default"}
                  size="small"
                  icon={<CheckCircle />}
                />
              )}
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                onClick={handleTokenTest}
                disabled={tokenTestResult.isLoading || tokenTestResult.isFetching}
              >
                {tokenTestResult.isLoading || tokenTestResult.isFetching ? (
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                ) : null}
                Test Token
              </Button>
              {tokenTestRan && tokenTestResult.data?.data && (
                <Stack direction="row" spacing={1} alignItems="center">
                  {tokenTestResult.data.data.success ? (
                    <Chip
                      label={`Success (${tokenTestResult.data.data.latencyMs}ms)`}
                      color="success"
                      size="small"
                      icon={<CheckCircle />}
                    />
                  ) : (
                    <Chip
                      label={`Failed: ${tokenTestResult.data.data.error || "Unknown error"}`}
                      color="error"
                      size="small"
                      icon={<ErrorIcon />}
                    />
                  )}
                </Stack>
              )}
              {tokenTestRan && tokenTestResult.isError && (
                <Chip
                  label="Request failed"
                  color="error"
                  size="small"
                  icon={<ErrorIcon />}
                />
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* API Connectivity Section */}
      <Card variant="outlined">
        <CardHeader title="API Connectivity" />
        <CardContent>
          <Stack direction="column" spacing={1}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2">API URL:</Typography>
              <Typography
                variant="caption"
                fontFamily="monospace"
                sx={{ bgcolor: "rgba(0, 0, 0, 0.06)", px: 1, borderRadius: 1 }}
              >
                {apiInfo?.sstv2ApiUrl || "N/A"}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2">{t("status")}:</Typography>
              {apiInfo?.isOnline ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: "green",
                    }}
                  />
                  <Typography variant="body2" color="green">
                    {t("online")}
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: "red",
                    }}
                  />
                  <Typography variant="body2" color="error">
                    {t("offline")}
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2">{t("latency")}:</Typography>
              <Typography variant="body2">
                {apiInfo?.pingLatency != null ? `${apiInfo.pingLatency}ms` : "N/A"}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Endpoint Health Section */}
      <Card variant="outlined">
        <CardHeader
          title="Endpoint Health"
          action={
            <Button variant="outlined" size="small" onClick={handleProbeAll}>
              Test All Endpoints
            </Button>
          }
        />
        <CardContent>
          <Stack direction="column" spacing={2}>
            <EndpointHealthRow
              label="Lockers"
              isLoading={qLockers.isLoading || qLockers.isFetching}
              isSuccess={probeLockers && qLockers.isSuccess}
              isError={probeLockers && qLockers.isError}
              probed={probeLockers}
            />
            <EndpointHealthRow
              label="Reservations"
              isLoading={qReservations.isLoading || qReservations.isFetching}
              isSuccess={probeReservations && qReservations.isSuccess}
              isError={probeReservations && qReservations.isError}
              probed={probeReservations}
            />
            <EndpointHealthRow
              label="SQS Configs"
              isLoading={qSqs.isLoading || qSqs.isFetching}
              isSuccess={probeSqs && qSqs.isSuccess}
              isError={probeSqs && qSqs.isError}
              probed={probeSqs}
            />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  )
}

const EndpointHealthRow: FC<{
  label: string
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
  probed: boolean
}> = ({ label, isLoading, isSuccess, isError, probed }) => {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography variant="body2" sx={{ minWidth: 120 }}>
        {label}
      </Typography>
      {!probed && (
        <Chip label="Not tested" size="small" variant="outlined" />
      )}
      {isLoading && <CircularProgress size={16} />}
      {isSuccess && (
        <Chip
          label="Reachable"
          color="success"
          size="small"
          icon={<CheckCircle />}
        />
      )}
      {isError && (
        <Chip
          label="Unreachable"
          color="error"
          size="small"
          icon={<ErrorIcon />}
        />
      )}
    </Stack>
  )
}

export default SaSstv2Diagnostics
